/** Any node in a CMS page model. Fields vary by componentType. */
export interface CMSNode {
  id?: string;
  componentType: string;
  [field: string]: unknown;
}

/** Contentful-style rich text node (document, paragraph, text, hyperlink, ...). */
export interface RichTextNode {
  nodeType: string;
  data?: { uri?: string; [key: string]: unknown };
  content?: RichTextNode[];
  value?: string;
  marks?: { type: string }[];
}

export interface PageModel extends CMSNode {
  metadata?: CMSNode;
  pageReference?: CMSNode;
  noIndex?: boolean;
}

export interface PageContext {
  space: string;
  pageId: string;
  isHome: boolean;
  /** Outline components that have no registered renderer. */
  debug: boolean;
  /** Cloudflare Turnstile public key for forms; unset disables the challenge */
  turnstileSiteKey?: string;
}

export interface SiteLink {
  label: string;
  href: string;
}

export type SocialIcon = "github" | "linkedin" | "mail" | "rss";

export interface SocialLink extends SiteLink {
  icon: SocialIcon;
}

/** Header, footer and SEO defaults shared by every page (Payload "site" global). */
export interface SiteSettings {
  header: { navLinks: SiteLink[] };
  footer: { copyright?: string };
  /** shown in the footer and under the contact form */
  social: { email?: string; links: SocialLink[] };
  seo: { siteName: string; description?: string; imageUrl?: string };
}

/** A published blog post without its body, for lists. */
export interface PostSummary {
  slug: string;
  title: string;
  /** ISO date */
  publishedAt: string;
  description: string;
  tags: string[];
  author?: string;
  readingTime: string;
}

/** A post as stored in the CMS: Markdown body, no derived fields. Server only. */
export interface PostSource extends Omit<PostSummary, "readingTime"> {
  body: string;
}

/** A post with its Markdown body rendered to HTML on the server. */
export interface Post extends PostSummary {
  html: string;
}

/** Serialized into the page and read back by entry-client for hydration. */
export interface RenderContext {
  pageModel: PageModel;
  pageContext: PageContext;
  site: SiteSettings;
  /** computed on the server so hydration never disagrees around New Year */
  year: number;
  componentResolutionMetadata: {
    /** componentTypes that appear on this page */
    componentTypes: string[];
  };
}

export const RENDER_CONTEXT_KEY = "__CMS_ENGINE_RENDER_CONTEXT__";

export const isCMSNode = (value: unknown): value is CMSNode =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as CMSNode).componentType === "string";

export const isExternal = (href: string) => /^https?:\/\//.test(href);

export const isRichText = (value: unknown): value is RichTextNode =>
  typeof value === "object" &&
  value !== null &&
  (value as RichTextNode).nodeType === "document";

export const str = (value: unknown) => (typeof value === "string" ? value : undefined);

export const formatDate = (iso: string, month: "short" | "long" = "short") =>
  new Date(iso).toLocaleDateString("en", {
    month,
    day: "numeric",
    year: "numeric",
    // dates are calendar days; UTC keeps server and browser in agreement
    timeZone: "UTC",
  });

/** One role on a résumé. `id` is the CMS array row id; tailoring keeps it. */
export interface ResumeRole {
  id: string;
  title: string;
  company: string;
  dates: string;
  location: string;
  summary: string;
  bullets: string[];
}

/**
 * A résumé (the CMS "resume" global, or a tailored copy). Email and phone are empty
 * on the public /cv page; the CMS only returns them to a signed-in reader.
 */
export interface Resume {
  name: string;
  headline: string;
  location: string;
  email: string;
  phone: string;
  links: SiteLink[];
  summary: string;
  skills: string[];
  experience: ResumeRole[];
  education: string;
  additional: string;
}

export interface ResumeAnalysis {
  matches: { requirement: string; evidence: string }[];
  gaps: string[];
  notes: string[];
}

export type ResumeTone = "natural" | "direct" | "confident";
export type ResumeMode = "ai" | "preview";

/** A tailored résumé saved in the CMS ("resume-drafts") by the local résumé agent. */
export interface ResumeDraft {
  id: number;
  title: string;
  jobTitle: string;
  company: string;
  tone: ResumeTone;
  mode: ResumeMode;
  requirement: string;
  guidance: string;
  resume: Resume;
  analysis: ResumeAnalysis | null;
  parent: number | null;
  createdAt: string;
  updatedAt: string;
}
